// About.js
// -----------------------------------------------------------------------------
// "Who We Are" story page. Warm intro hero, a relational welcome statement, and
// the NHV / Vineyard history told as readable prose. All original copy, the
// external Vineyard link, and the worship-team photo are preserved.
// -----------------------------------------------------------------------------

import AspectRatio from '@mui/joy/AspectRatio';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Link from '@mui/joy/Link';

import { Section, SectionHeading, PageHero } from '../components/ui';
import WhoWeAre from '../assets/WhoWeAre.jpg';
import './styles/About.css';

// External worship-team image (off-site asset preserved from the original page).
const WORSHIP_IMAGE_URL =
  'https://nhvineyard.com/wp-content/uploads/2018/04/NHV-Worship-Team-8119.jpg';
const VINEYARD_USA_URL = 'https://vineyardusa.org/';

const About = () => {
  return (
    <Box>
      <PageHero
        image={WhoWeAre}
        eyebrow="New Horizon Vineyard"
        title="Who We Are"
        subtitle="A community committed to relationship with God and with each other. We aren't perfect, but we are real."
        height="md"
        overlay="medium"
      />

      <Section bg="default" padding="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 4, md: 8 }}
          sx={{ alignItems: 'flex-start' }}
        >
          <Stack spacing={2.5} sx={{ flex: 1.3 }}>
            <SectionHeading
              eyebrow="Our Story"
              title="There's Always Room for One More"
            />
            <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
              New Horizon Vineyard is a community committed to relationship. Relationship with God
              and each other. We know that if we start here, we will end up changing the world. We
              aren&rsquo;t perfect but we are real. We don&rsquo;t hide our imperfections but
              understand that part of authentic community is transparency and vulnerability. So we
              invite you into the story we are writing here at NHV.
            </Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary' }}>
              In 1990, a group of 35 individuals joined together to form Providence Presbyterian
              Church. They were a congregation without a pastor meeting in the fellowship hall of a
              Baptist Church in Garland. Cliff was a former youth pastor in Denver, Colorado, and had
              just recently moved back to Dallas to pursue a spot on the PGA Tour. Through a divinely
              orchestrated set of events, the Lord told Cliff that the PGA Tour was a great idea but
              not His idea and that he needed to get back to his calling as a pastor. God brought
              together the pastor and the congregation in November 1990.
            </Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary' }}>
              The group went from that fellowship hall to Lakeview Centennial High School to an
              office complex off of I-30 &amp; Bobtown Road. Each step was a step of growth, both
              physically and spiritually. The trail ultimately led to the purchase of nearly 9 acres
              and the building of the present facility at Dalrock &amp; Schrade in 1997. In 2000, the
              leadership of Providence Church unanimously voted to leave the Evangelical Presbyterian
              Church Denomination and become part of the Association of Vineyard Churches. The move
              unified the external name with the internal make-up of the church. Providence
              didn&rsquo;t become a Vineyard, it just recognized that it already was one. In April
              2001 Providence Church was officially accepted into the Association of Vineyard
              Churches as New Horizon Vineyard Church.
            </Typography>
          </Stack>

          <Box sx={{ flex: 1, width: '100%' }}>
            <AspectRatio
              ratio="4/5"
              sx={{ borderRadius: 'lg', boxShadow: 'md', position: { md: 'sticky' }, top: 24 }}
            >
              <img className="about-photo" src={WORSHIP_IMAGE_URL} alt="NHV worship team" loading="lazy" />
            </AspectRatio>
          </Box>
        </Stack>
      </Section>

      <Section bg="tint" padding="lg">
        <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center' }}>
          <Typography level="title" sx={{ color: 'primary.600', mb: 1.5 }}>
            The Vineyard Movement
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary', mb: 2 }}>
            The{' '}
            <Link
              href={VINEYARD_USA_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.600' }}
            >
              National Vineyard Website
            </Link>{' '}
            introduces the Vineyard movement by saying:
          </Typography>
          <Typography
            level="h4"
            sx={{ color: 'text.primary', fontStyle: 'italic', fontWeight: 'normal', mb: 2 }}
          >
            &ldquo;The Vineyard story is about ordinary people who worship and serve an extraordinary
            God. The Vineyard is simply one thread in the rich tapestry of the historic and global
            Church of Jesus Christ. But it is a thread of God&rsquo;s weaving. From the beginning,
            Vineyard pastors and leaders have sought to hold in tension the biblical doctrines of the
            Christian faith with an ardent pursuit of the present day work of the Spirit of
            God.&rdquo;
          </Typography>
          <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
            We continue to embrace that vision.
          </Typography>
        </Box>
      </Section>
    </Box>
  );
};

export default About;
